type BillToLike = {
  clientType: 'Individual' | 'Corporate' | null;
  clientDetails: Record<string, unknown> | null;
  countryCode?: string | null;
  name: string | null;
  email: string | null;
};

const PRINTED_DETAILS: Record<'Individual' | 'Corporate', [string, string][]> =
  {
    Individual: [
      ['fullName', 'Full name'],
      ['documentNumber', 'Document number'],
      ['legalAddress', 'Legal address'],
      ['residenceCountry', 'Residence country'],
    ],
    Corporate: [
      ['companyName', 'Company name'],
      ['companyRegNumber', 'Registration number'],
      ['companyTaxId', 'Tax id'],
      ['companyCountry', 'Country of incorporation'],
      ['companyLegalAddress', 'Legal address'],
    ],
  };

const show = (v: unknown) =>
  v == null || String(v).trim() === '' ? '-' : String(v).trim();

/** The bill-to as the document prints it, latest version against the payer now. */
export function billToRows(before: BillToLike | null, after: BillToLike) {
  const rows: [string, unknown, unknown][] = [
    ['Name', before?.name, after.name],
    ['Type', before?.clientType, after.clientType],
    ['Country', before?.countryCode, after.countryCode],
    ['Invoice email', before?.email, after.email],
  ];
  const types = new Set(
    [before?.clientType, after.clientType].filter(
      (t): t is 'Individual' | 'Corporate' => t != null,
    ),
  );
  const seen = new Set<string>();
  for (const type of types) {
    for (const [key, label] of PRINTED_DETAILS[type]) {
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push([
        label,
        before?.clientDetails?.[key],
        after.clientDetails?.[key],
      ]);
    }
  }
  return rows
    .map(([label, b, a]) => ({ label, before: show(b), after: show(a) }))
    .filter((r) => r.before !== '-' || r.after !== '-');
}

type CardSource = {
  clientType: 'Individual' | 'Corporate' | null;
  details: object | null;
};

export type PayerCardFacts = {
  name: string;
  clientType: 'Individual' | 'Corporate' | null;
  details: object | null;
  email: string | null;
  version: number | null;
};

const legalName = (source: CardSource) => {
  const key = source.clientType === 'Corporate' ? 'companyName' : 'fullName';
  const value = (source.details as Record<string, unknown> | null)?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : '';
};

export function payerOnCard<B extends BillToLike>(
  versions: { version: number; billTo: B }[] | undefined,
  clientInput: CardSource | null | undefined,
): PayerCardFacts | null {
  const latest = versions?.length ? versions[versions.length - 1] : null;
  if (latest) {
    const { billTo } = latest;
    const source = {
      clientType: billTo.clientType,
      details: billTo.clientDetails,
    };
    return {
      name: legalName(source) || billTo.name || '',
      clientType: billTo.clientType,
      details: billTo.clientDetails,
      email: billTo.email,
      version: latest.version,
    };
  }
  if (!clientInput) return null;
  return {
    name: legalName(clientInput),
    clientType: clientInput.clientType,
    details: clientInput.details,
    email: null,
    version: null,
  };
}
